import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {DetectMethodChanges} from '../utils';
import {HedgePresetClient} from './hedge-preset-client.service';
import {HedgePreset, HedgePresetSelection, HedgeMode, HedgeExecutionSide, eligibleHedgePresets} from './hedge-preset.model';
import {MessageBusService} from '../message-bus.service';
import {Subscription} from 'rxjs';

@Component({selector: 'ets-hedge-preset-selector', templateUrl: './hedge-preset-selector.component.html',
    styleUrls: ['./hedge-presets.scss'], changeDetection: ChangeDetectionStrategy.OnPush})
export class HedgePresetSelectorComponent implements OnChanges, OnDestroy {
    @Input() underlying: string;
    @Input() selection: HedgePresetSelection;
    @Input() disabled = false;
    @Input() side: HedgeExecutionSide;
    @Input() apgSource: string = null;
    @Input() layout: 'columns' | 'vertical' = 'columns';
    @Output() changed = new EventEmitter<void>();
    presets: HedgePreset[] = [];
    loading = false;
    available = false;
    error = '';
    private buttons: {id: HedgeMode; text: string; disabled?: boolean}[];
    get modeButtons(): {id: HedgeMode; text: string; disabled?: boolean}[] {
        const blocked = this.loading || !this.client.canUse || !this.available;
        if (!this.buttons || this.buttons[1].disabled !== blocked) {
            this.buttons = [{id: 'custom', text: 'Custom'}, {id: 'preset', text: 'Preset', disabled: blocked}];
        }
        return this.buttons;
    }
    get selectedModeButtons(): object[] {
        return this.modeButtons.filter(x => x.id === (this.selection && this.selection.mode === 'preset' ? 'preset' : 'custom'));
    }
    private epoch = 0;
    private settingsSubscription: Subscription;
    constructor(public readonly client: HedgePresetClient, private readonly _changeDetector: ChangeDetectorRef,
        messages: MessageBusService = null) {
        if (messages) {
            this.settingsSubscription = messages.of<any>('UserSettingsChangedDto').subscribe(message => {
                if (this.apgSource && (message.payload.settings || []).some(s =>
                    s.key === 'apg.templates' || s.key === 'apg.portfolios' || s.key === 'hg.independent-portfolios'
                    || s.key.indexOf('apg.last-used-template.') === 0)) { this.reload(); }
            });
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.underlying || changes.selection || changes.side || changes.apgSource) { this.reload(); }
    }
    ngOnDestroy(): void { ++this.epoch; if (this.settingsSubscription) { this.settingsSubscription.unsubscribe(); } }
    @DetectMethodChanges({isAsync: true}) async reload(): Promise<void> {
        const epoch = ++this.epoch;
        this.available = false; this.presets = []; this.loading = false;
        if (this.selection) { this.selection.presetValid = false; }
        if (!this.underlying || (!this.client.canUse && !this.client.canManage)) {
            this.presets = []; this.error = this.selection && this.selection.mode === 'preset' ? 'Preset Access Is Unavailable.' : '';
            this.changed.emit(); return;
        }
        this.loading = true; this.error = ''; this._changeDetector.detectChanges();
        this.changed.emit();
        try {
            const rows = await this.client.list(this.underlying, this.side, this.apgSource);
            if (epoch !== this.epoch) { return; }
            this.presets = eligibleHedgePresets(rows, this.side);
            this.available = true;
            this.checkSelection();
        } catch (e) { if (epoch === this.epoch) { this.error = e.message || 'Could Not Load Presets.'; } }
        finally { if (epoch === this.epoch) { this.loading = false; this.changed.emit(); } }
    }
    private checkSelection(): void {
        if (!this.selection) { return; }
        if (this.selection.mode && this.selection.mode !== 'custom' && this.selection.mode !== 'preset') {
            this.selection.presetValid = false; this.error = 'Saved Hedge Mode Is Unsupported.'; return;
        }
        const preset = this.presets.filter(p => p.id === this.selection.presetId)[0];
        this.selection.presetValid = this.client.canUse && !!preset && !preset.requiresReconfiguration;
        this.selection.presetName = preset ? preset.name : null;
        if (this.selection.mode === 'preset' && preset && preset.requiresReconfiguration) {
            this.error = 'Selected Preset Requires Reconfiguration. Ask an Administrator to Edit and Save It.'; return;
        }
        if (this.selection.mode === 'preset' && this.selection.presetId && !this.selection.presetValid) {
            this.error = 'Selected Preset Is Unavailable for This Underlying, Side or Destination Spread Size.';
        }
    }
    @DetectMethodChanges() mode(mode: HedgeMode): void {
        if (this.disabled || !this.selection || (mode === 'preset' && (!this.client.canUse || this.loading || !this.available))) { return; }
        this.selection.mode = mode; this.error = ''; this.checkSelection(); this.changed.emit();
        if (mode === 'preset') { this.reload(); }
    }
    @DetectMethodChanges() select(id: string): void {
        if (this.disabled || this.loading || !this.client.canUse || !this.available || !this.selection
            || !this.presets.some(p => p.id === id && !p.requiresReconfiguration)) { return; }
        this.selection.presetId = id || null; this.error = ''; this.checkSelection(); this.changed.emit();
    }
    trackPreset(index: number, preset: HedgePreset): string { return preset.id; }
}
